import React, { createContext, useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, Info, X, Undo2 } from 'lucide-react';
import { cn } from '../utils/cn';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
}

interface ShowToastOptions {
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextType {
  toast: (message: string, options?: ShowToastOptions) => number;
  dismiss: (id: number) => void;
  success: (message: string, options?: Omit<ShowToastOptions, 'variant'>) => number;
  error: (message: string, options?: Omit<ShowToastOptions, 'variant'>) => number;
  info: (message: string, options?: Omit<ShowToastOptions, 'variant'>) => number;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

const variantStyles: Record<ToastVariant, { icon: React.ComponentType<any>; cls: string; iconCls: string }> = {
  success: {
    icon: CheckCircle2,
    cls: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    iconCls: 'text-emerald-600',
  },
  error: {
    icon: AlertCircle,
    cls: 'border-red-200 bg-red-50 text-red-900',
    iconCls: 'text-red-600',
  },
  info: {
    icon: Info,
    cls: 'border-line bg-surface text-ink',
    iconCls: 'text-primary-600',
  },
};

let nextId = 1;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, options: ShowToastOptions = {}) => {
      const id = nextId++;
      const variant = options.variant || 'info';
      const duration = options.duration ?? (options.action ? 6000 : 3500);
      setToasts((prev) => [...prev, { id, message, variant, action: options.action }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const success = useCallback(
    (m: string, o: Omit<ShowToastOptions, 'variant'> = {}) => toast(m, { ...o, variant: 'success' }),
    [toast]
  );
  const error = useCallback(
    (m: string, o: Omit<ShowToastOptions, 'variant'> = {}) => toast(m, { ...o, variant: 'error' }),
    [toast]
  );
  const info = useCallback(
    (m: string, o: Omit<ShowToastOptions, 'variant'> = {}) => toast(m, { ...o, variant: 'info' }),
    [toast]
  );

  return (
    <ToastContext.Provider value={{ toast, dismiss, success, error, info }}>
      {children}
      {createPortal(
        <div className="fixed inset-x-0 bottom-24 sm:bottom-6 z-[9999] flex flex-col items-center gap-2 px-4 pointer-events-none">
          {toasts.map((t) => {
            const { icon: Icon, cls, iconCls } = variantStyles[t.variant];
            return (
              <div
                key={t.id}
                role="status"
                className={cn(
                  'pointer-events-auto flex items-center gap-3 max-w-md w-full rounded-2xl border px-3.5 py-2.5 shadow-lifted animate-slide-up',
                  cls
                )}
              >
                <Icon className={cn('w-4 h-4 shrink-0', iconCls)} />
                <span className="flex-1 text-sm">{t.message}</span>
                {t.action && (
                  <button
                    onClick={() => {
                      t.action!.onClick();
                      dismiss(t.id);
                    }}
                    className="inline-flex items-center gap-1 text-sm font-semibold underline-offset-2 hover:underline"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    {t.action.label}
                  </button>
                )}
                <button
                  onClick={() => dismiss(t.id)}
                  className="opacity-60 hover:opacity-100"
                  aria-label="Закрыть"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};
